import { Plus, Trash2 } from 'lucide-react';
import { useDestinataires, useParametres, type InstructionSaisie } from '@/api/hooks';
import { LIBELLE_PRIORITE, LIBELLE_TYPE_INSTRUCTION, type Priorite, type TypeInstruction } from '@/api/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SelectNatif } from '@/components/ui/select-natif';
import { Textarea } from '@/components/ui/textarea';

/** Instructions types (bibliothèque paramétrable, spécification Courrier v1.0, § 8.1). */
export const INSTRUCTIONS_TYPES = [
  'Me faire un rapport sous huitaine.',
  'Pour suite à donner et compte rendu.',
  'Préparer une réponse pour ma signature.',
  'Pour information et classement.',
  'Traiter en urgence et me tenir informé.',
  'Organiser une réunion et me proposer une date.',
];

export const instructionVide = (): InstructionSaisie => ({ responsableId: '', type: 'ACTION', texte: '', echeance: undefined });

/** Champs d'une instruction : destinataire, type, échéance, texte (avec la bibliothèque d'instructions types). */
export function InstructionChamps({ valeur, onChange, priorite = false }: { valeur: InstructionSaisie; onChange: (v: InstructionSaisie) => void; priorite?: boolean }) {
  const destinataires = useDestinataires();
  const parametres = useParametres();
  // Bibliothèque administrée (paramètre « instructions types »), à défaut celle des spécifications.
  const bibliotheque = (parametres.data?.find((p) => p.cle === 'courrier.instructionsTypes')?.valeur as string[] | undefined) ?? INSTRUCTIONS_TYPES;
  const maj = (patch: Partial<InstructionSaisie>) => onChange({ ...valeur, ...patch });
  return (
    <div className="space-y-2">
      <div className="grid gap-2 md:grid-cols-[1fr_11rem_10rem]">
        <SelectNatif value={valeur.responsableId} onChange={(e) => maj({ responsableId: e.target.value })} aria-label="Destinataire">
          <option value="">Destinataire</option>
          {(destinataires.data ?? []).map((d) => (
            <option key={d.agentId} value={d.agentId}>
              {d.nom} · {d.fonction}
            </option>
          ))}
        </SelectNatif>
        <SelectNatif value={valeur.type} onChange={(e) => maj({ type: e.target.value as TypeInstruction, echeance: e.target.value === 'INFORMATION' ? undefined : valeur.echeance })} aria-label="Type">
          {(Object.keys(LIBELLE_TYPE_INSTRUCTION) as TypeInstruction[]).map((t) => (
            <option key={t} value={t}>
              {LIBELLE_TYPE_INSTRUCTION[t]}
            </option>
          ))}
        </SelectNatif>
        <Input type="date" value={valeur.echeance ?? ''} disabled={valeur.type === 'INFORMATION'} onChange={(e) => maj({ echeance: e.target.value || undefined })} title="Échéance (proposée selon la priorité si vide)" aria-label="Échéance" />
      </div>
      {priorite && (
        <SelectNatif value={valeur.priorite ?? 'NORMALE'} onChange={(e) => maj({ priorite: e.target.value as Priorite })} aria-label="Priorité">
          {(Object.keys(LIBELLE_PRIORITE) as Priorite[]).map((p) => (
            <option key={p} value={p}>
              Priorité {LIBELLE_PRIORITE[p].toLowerCase()}
            </option>
          ))}
        </SelectNatif>
      )}
      <Textarea rows={2} value={valeur.texte} onChange={(e) => maj({ texte: e.target.value })} placeholder="Texte de l'instruction" />
      <div className="flex flex-wrap gap-1">
        {bibliotheque.map((t) => (
          <button key={t} type="button" className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted" onClick={() => maj({ texte: t })}>
            {t}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Saisie d'une liste d'instructions : un destinataire, un type, un texte, une échéance par ligne. */
export function InstructionsBuilder({ valeur, onChange }: { valeur: InstructionSaisie[]; onChange: (v: InstructionSaisie[]) => void }) {
  return (
    <div className="space-y-3">
      {valeur.map((ins, i) => (
        <div key={i} className="relative rounded-lg border p-3 pr-12">
          <InstructionChamps valeur={ins} onChange={(v) => onChange(valeur.map((x, k) => (k === i ? v : x)))} />
          <Button type="button" variant="ghost" size="icon" className="absolute right-2 top-2" onClick={() => onChange(valeur.filter((_, k) => k !== i))} aria-label="Retirer">
            <Trash2 className="size-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => onChange([...valeur, instructionVide()])}>
        <Plus className="size-4" /> Ajouter un destinataire
      </Button>
    </div>
  );
}
